import React, { useEffect, useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Users as UsersIcon, ShieldCheck, Save, Pencil } from "lucide-react";
import { toast } from "sonner";

const PERM_LABELS = {
  create_sales: "Create Sales",
  cancel_invoice: "Cancel Invoice",
  delete_invoice: "Delete Invoice",
  view_financials: "View Financials",
  manage_settings: "Manage Settings",
  manage_inventory: "Manage Inventory",
  manage_masters: "Manage Masters (Parties/Products)",
  manage_users: "Manage Users & Roles",
};

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const BUILT_IN_ROLES = new Set([
  "Super Admin", "Admin", "Accounts Manager", "Accountant", "Sales Manager",
  "Sales Executive / MR", "Inventory Manager", "Viewer", "Auditor",
]);

export default function UsersRoles() {
  const [users, setUsers] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [roles, setRoles] = useState({});
  const [allPerms, setAllPerms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "Viewer" });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: "", phone: "", role: "Viewer", active: true });
  const [roleDialog, setRoleDialog] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", permissions: [] });
  const [savingRoles, setSavingRoles] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [usersLoadError, setUsersLoadError] = useState("");
  const [rolesLoadError, setRolesLoadError] = useState("");

  const loadUsers = () => {
    setUsersLoadError("");
    return http.get("/users").then((r) => setUsers(r.data)).catch((e) => setUsersLoadError(requestErrorMessage(e, "load company users")));
  };
  const loadRoles = () => {
    setRolesLoadError("");
    return http.get("/roles").then((r) => { setRoles(r.data.roles); setAllPerms(r.data.all_perms); setRoleNames(r.data.role_names); }).catch((e) => setRolesLoadError(requestErrorMessage(e, "load roles and permissions")));
  };
  useEffect(() => { loadUsers(); loadRoles(); }, []);

  const addUser = async () => {
    if (savingUser) return;
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setSavingUser(true);
    try {
      await http.post("/users", form);
      toast.success("User added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "Viewer" });
      loadUsers();
    } catch (e) {
      toast.error(requestErrorMessage(e, "add this user"));
    } finally {
      setSavingUser(false);
    }
  };
  const toggleActive = async (u) => {
    try { await http.put(`/users/${u.user_id}`, { active: !u.active }); loadUsers(); }
    catch (e) { toast.error(requestErrorMessage(e, "update this user's status")); }
  };
  const startEditUser = (u) => {
    setEditingUser(u);
    setEditUserForm({ name: u.name || "", phone: u.phone || "", role: u.role, active: Boolean(u.active) });
  };
  const saveUserEdit = async () => {
    try {
      await http.put(`/users/${editingUser.user_id}`, editUserForm);
      toast.success("User updated");
      setEditingUser(null);
      loadUsers();
    } catch (e) { toast.error(requestErrorMessage(e, "update this user")); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email} from this company?`)) return;
    try { await http.delete(`/users/${u.user_id}`); toast.success("User removed"); loadUsers(); }
    catch (e) { toast.error(requestErrorMessage(e, "remove this user")); }
  };

  const togglePerm = (role, perm) => {
    setRoles((prev) => {
      const cur = new Set(prev[role] || []);
      cur.has(perm) ? cur.delete(perm) : cur.add(perm);
      return { ...prev, [role]: [...cur] };
    });
  };
  const saveRoles = async () => {
    setSavingRoles(true);
    try { const { data } = await http.put("/roles", { roles }); setRoles(data.roles); setRoleNames(data.role_names || roleNames); toast.success("Permissions saved"); }
    catch (e) { toast.error(requestErrorMessage(e, "save role permissions")); }
    finally { setSavingRoles(false); }
  };
  const openRoleEditor = (role = null) => {
    setRoleDialog(role ? { mode: "edit", role } : { mode: "add", role: null });
    setRoleForm({ name: role || "", permissions: role ? [...(roles[role] || [])] : [] });
  };
  const toggleRoleFormPerm = (perm) => setRoleForm((current) => ({
    ...current,
    permissions: current.permissions.includes(perm)
      ? current.permissions.filter((item) => item !== perm)
      : [...current.permissions, perm],
  }));
  const saveRole = async () => {
    const name = roleForm.name.trim();
    if (!name) return toast.error("Role name is required");
    try {
      if (roleDialog.mode === "add") {
        await http.post("/roles", { name, permissions: roleForm.permissions });
        toast.success("Role added");
      } else {
        await http.put(`/roles/${encodeURIComponent(roleDialog.role)}`, {
          ...(name !== roleDialog.role ? { name } : {}),
          permissions: roleForm.permissions,
        });
        toast.success("Role updated");
      }
      setRoleDialog(null);
      loadRoles();
    } catch (e) { toast.error(requestErrorMessage(e, "save this role")); }
  };
  const deleteRole = async (role) => {
    if (!window.confirm(`Delete the custom role "${role}"? Users assigned to this role may need a new role.`)) return;
    try {
      await http.delete(`/roles/${encodeURIComponent(role)}`);
      toast.success("Role deleted");
      loadRoles();
    } catch (e) { toast.error(requestErrorMessage(e, "delete this role")); }
  };

  return (
    <Tabs defaultValue="users" data-testid="users-roles">
      <TabsList>
        <TabsTrigger value="users" data-testid="tab-all-users"><UsersIcon className="h-4 w-4 mr-1" /> All Users</TabsTrigger>
        <TabsTrigger value="roles" data-testid="tab-roles-perms"><ShieldCheck className="h-4 w-4 mr-1" /> Roles &amp; Permissions</TabsTrigger>
      </TabsList>

      <TabsContent value="users" className="mt-4 space-y-3">
        {usersLoadError && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{usersLoadError} <Button variant="outline" size="sm" className="ml-2" onClick={loadUsers}>Retry</Button></div>}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-muted-foreground">Add your staff. Assign roles. Multiply your business.</p>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" /> Add User</Button>
        </div>
        <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Mobile</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Role</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="h-8 w-8 rounded-full bg-brand-100 text-brand-700 grid place-items-center text-xs font-semibold">{initials(u.name)}</span>
                    <span className="font-medium">{u.name || "—"}{u.is_self && <span className="ml-1.5 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">you</span>}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.phone || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">{u.role}{u.permanent && <span className="block text-xs text-muted-foreground">Permanent administrator</span>}</td>
                <td className="px-4 py-3">
                  <button disabled={u.permanent} title={u.permanent ? "Permanent administrator access is protected" : undefined} onClick={() => toggleActive(u)} data-testid={`user-active-${u.user_id}`}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border ${u.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {u.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button type="button" size="sm" variant="ghost" onClick={() => startEditUser(u)} data-testid={`user-edit-${u.user_id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                  <Button type="button" size="sm" variant="ghost" disabled={u.is_self || u.permanent} onClick={() => removeUser(u)} title={u.permanent ? "Permanent administrator access is protected" : u.is_self ? "You cannot remove your own account" : "Delete user"} aria-label={`Delete ${u.name || u.email}`} data-testid={`user-delete-${u.user_id}`}><Trash2 className="h-4 w-4 text-rose-500" /> <span className="sr-only">Delete</span></Button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground"><UsersIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />No users yet</td></tr>}
          </tbody>
        </table></div></Card>
      </TabsContent>

      <TabsContent value="roles" className="mt-4 space-y-4">
        {rolesLoadError && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{rolesLoadError} <Button variant="outline" size="sm" className="ml-2" onClick={loadRoles}>Retry</Button></div>}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-muted-foreground">Toggle permissions per role. Super Admin always has full access.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openRoleEditor()} data-testid="add-role-button"><Plus className="h-4 w-4 mr-1" /> Add Role</Button>
            <Button size="sm" onClick={saveRoles} disabled={savingRoles} data-testid="save-roles-button"><Save className="h-4 w-4 mr-1" /> {savingRoles ? "Saving…" : "Save Permissions"}</Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {roleNames.map((role) => {
            const locked = role === "Super Admin";
            const perms = new Set(roles[role] || []);
            return (
              <Card key={role} className="p-4" data-testid={`role-card-${role.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-3"><ShieldCheck className={`h-4 w-4 ${locked ? "text-amber-500" : "text-brand-500"}`} /><span className="font-heading font-semibold text-sm">{role}</span>
                  {locked && <span className="ml-auto text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">full access</span>}
                  {!locked && !BUILT_IN_ROLES.has(role) && <div className="ml-auto flex gap-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => openRoleEditor(role)} data-testid={`edit-role-${role}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => deleteRole(role)} data-testid={`delete-role-${role}`}><Trash2 className="h-4 w-4 text-rose-600 mr-1" /> Delete</Button>
                  </div>}
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {allPerms.map((p) => (
                    <label key={p} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded ${locked ? "opacity-60" : "hover:bg-slate-50 cursor-pointer"}`}>
                      <input type="checkbox" checked={locked ? true : perms.has(p)} disabled={locked}
                        onChange={() => togglePerm(role, p)} className="h-4 w-4 accent-brand-600"
                        data-testid={`perm-${role.replace(/[^a-z]+/gi, "-").toLowerCase()}-${p}`} />
                      <span>{PERM_LABELS[p] || p}</span>
                    </label>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      </TabsContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
            <div><Label>Mobile</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="user-phone-input" /></div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="Required for new users" data-testid="user-password-input" />
              <p className="mt-1 text-xs text-muted-foreground">
                {users.some((user) => user.email?.toLowerCase() === form.email.trim().toLowerCase())
                  ? "This person is already a member of this company; a password is not needed."
                  : "New users need a password. If this person already has an account in another company, you can leave it blank."}
              </p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roleNames.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" disabled={savingUser} onClick={() => setOpen(false)}>Cancel</Button><Button onClick={addUser} disabled={savingUser} data-testid="save-user-button">{savingUser ? "Adding…" : "Add User"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(editingUser)} onOpenChange={(value) => !value && setEditingUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Name</Label><Input value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} className="mt-1.5" data-testid="edit-user-name-input" /></div>
            <div><Label>Mobile</Label><Input value={editUserForm.phone} onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })} className="mt-1.5" data-testid="edit-user-phone-input" /></div>
            <div><Label>Role</Label><Select disabled={editingUser?.permanent} value={editUserForm.role} onValueChange={(role) => setEditUserForm({ ...editUserForm, role })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{roleNames.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}</SelectContent>
            </Select></div>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" disabled={editingUser?.permanent} checked={editUserForm.active} onChange={(e) => setEditUserForm({ ...editUserForm, active: e.target.checked })} className="h-4 w-4 accent-brand-600" /> Active</label>
            <p className="sm:col-span-2 text-xs text-muted-foreground">Email and password are not changed here.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button><Button onClick={saveUserEdit} data-testid="save-user-edit-button">Save User</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(roleDialog)} onOpenChange={(value) => !value && setRoleDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{roleDialog?.mode === "add" ? "Add Role" : "Edit Custom Role"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Role name</Label><Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} className="mt-1.5" data-testid="role-name-input" /></div>
            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {allPerms.map((perm) => <label key={perm} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={roleForm.permissions.includes(perm)} onChange={() => toggleRoleFormPerm(perm)} className="h-4 w-4 accent-brand-600" />
                  {PERM_LABELS[perm] || perm}
                </label>)}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRoleDialog(null)}>Cancel</Button><Button onClick={saveRole}>{roleDialog?.mode === "add" ? "Add Role" : "Save Role"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
